import React from 'react';
import { Badge, Button, Sym } from '../../../components/app2';
import { formatDateSafe } from '../../../utils/dateUtils';

/**
 * Tarjeta de recibo del portal del empleado (rediseño jul-2026).
 *
 * Sigue la tarjeta del diseño (periodo · estado · Bruto/Deducciones/Neto · botón
 * de descarga) y conserva todo lo que ya mostraba la versión anterior y el
 * diseño no dibuja: folio, rango del periodo, días pagados, fecha de emisión,
 * UUID del CFDI y la fecha de descarga. Un recibo cancelado sigue con el botón
 * deshabilitado.
 */

const STATUS = {
  downloaded: { label: 'Disponible', tone: 'ok', icon: 'check_circle' },
  viewed: { label: 'Cancelado', tone: 'warn', icon: 'block' },
  pending: { label: 'Pendiente', tone: 'brand', icon: 'schedule' },
};

const Row = ({ label, value, strong = false }) => (
  <div
    style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      gap: 12, padding: strong ? '9px 0 12px' : '6px 0',
      borderTop: '1px solid var(--line)',
    }}
  >
    <span style={{ font: `${strong ? 700 : 500} .86rem 'Hanken Grotesk'`, color: strong ? 'var(--heading)' : 'var(--muted)' }}>
      {label}
    </span>
    <span
      className={strong ? 'a2-serif' : 'a2-mono'}
      style={strong
        ? { fontSize: '1.2rem', fontWeight: 600, color: 'var(--brand-2)' }
        : { fontSize: '.86rem', fontWeight: 600, color: 'var(--text)' }}
    >
      {value}
    </span>
  </div>
);

const Meta = ({ icon, children, title }) => (
  <div
    title={title}
    style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}
  >
    <Sym name={icon} size={14} style={{ color: 'var(--faint)', flexShrink: 0 }} />
    <span className="a2-truncate" style={{ font: "500 .74rem 'Hanken Grotesk'", color: 'var(--muted)' }}>
      {children}
    </span>
  </div>
);

const PayrollReceiptCard = ({ receipt, onDownload }) => {
  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: receipt?.currency || 'MXN',
      minimumFractionDigits: 2,
    }).format(amount || 0);

  const status = STATUS[receipt?.status] || { label: 'Sin estado', tone: 'neutral', icon: 'help' };
  const isCancelled = receipt?.status === 'viewed';

  return (
    <div className="a2-card" style={{ padding: 18, display: 'flex', flexDirection: 'column' }}>
      {/* Periodo + estado. El rango de fechas va abajo, con el resto de los
          datos: aquí competía con el badge y se truncaba en tarjetas angostas. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
        <div className="a2-truncate" style={{ font: "700 1.02rem 'Hanken Grotesk'", color: 'var(--heading)', minWidth: 0 }}>
          {receipt?.payPeriod}
        </div>
        <Badge tone={status.tone} icon={status.icon}>{status.label}</Badge>
      </div>

      {/* Importes */}
      <Row label="Bruto" value={formatCurrency(receipt?.grossSalary)} />
      <Row label="Deducciones" value={formatCurrency(receipt?.deductions)} />
      <Row label="Neto" value={formatCurrency(receipt?.totalAmount)} strong />

      {/* Datos fiscales y de trazabilidad */}
      <div
        style={{
          display: 'flex', flexDirection: 'column', gap: 5, paddingBottom: 14,
          borderTop: '1px solid var(--line)', paddingTop: 10,
        }}
      >
        <Meta
          icon="date_range"
          title={`Periodo del ${formatDateSafe(receipt?.periodStart)} al ${formatDateSafe(receipt?.periodEnd)}`}
        >
          {formatDateSafe(receipt?.periodStart)} — {formatDateSafe(receipt?.periodEnd)}
        </Meta>
        {receipt?.folio ? <Meta icon="tag">Folio {receipt.folio}</Meta> : null}
        <Meta icon="event">Emitido {formatDateSafe(receipt?.uploadDate)}</Meta>
        {receipt?.daysPaid ? <Meta icon="calendar_month">{receipt.daysPaid} días pagados</Meta> : null}
        {receipt?.uuid ? (
          <Meta icon="verified_user" title={`UUID ${receipt.uuid}`}>UUID {receipt.uuid}</Meta>
        ) : null}
        {receipt?.downloadDate ? (
          <Meta icon="download_done">Descargado {formatDateSafe(receipt.downloadDate)}</Meta>
        ) : null}
      </div>

      <Button
        variant="primary"
        icon="picture_as_pdf"
        onClick={() => onDownload?.(receipt)}
        disabled={isCancelled}
        style={{ width: '100%', marginTop: 'auto' }}
      >
        {isCancelled ? 'Cancelado' : 'Descargar PDF'}
      </Button>
    </div>
  );
};

export default PayrollReceiptCard;
